// Routing rules (Admin → Routing rules, `routing-rule`, 019): which numbers a pick ticket's or an
// SO's routing window counts with (`window.ts`: Window opens = FIRST SHIP DATE − open days, Route by
// = CANCEL DATE − due days, in business days). One rule, `routingRuleFor`; the first that applies wins:
//   1. the app's rules of its shown-name group (Admin → Customers): its own record, and the records
//      of the other codes showing the same name — set once on a group, a new code has it at once;
//   2. A2000's CUST_REF1 / CUST_REF2 of its group: its own code's, and the other A2000 codes'
//      showing the same name (how a Deposco customer gets A2000's rule);
//   3. a Deposco customer only: the A2000 customer with exactly its name among the open pick tickets
//      (the borrow from before the app's rules, kept so nothing loses the rule it had);
//   4. none: "No routing rule".
// Codes of a group that disagree give the strictest: the larger open and the larger due days (both
// set earlier dates), so a group shares one window. A record's `noRule` is "No routing rule" set in
// the app: it wins over A2000's, but in a group a code with days wins over it (a window is stricter
// than none) — No rule only when it is the group's only app rule. Empty days are only "no app days".
// A record's `separate` keeps its code out of its group: it counts with its own only (its app days,
// else its own CUST_REF1/2, else the Deposco borrow, else none; `noRule`: none), and its values are
// left out of the group's strictest. A2000's values are never written back.

import type { PtView } from "./assemble";
import { customerKey } from "./assignments";
import type { CustomerNamer } from "./customer-names";
import { customerNamer, nameKey } from "./customer-names";
import { formatShortDay } from "./days";
import type { LineSource } from "./deposco";
import type { CustomerNameRecord, RoutingRuleRecord, SourceRow } from "./types";

/** A day count's largest: Window opens and Route by, 0–60 business days. */
export const RULE_DAYS_MAX = 60;

/** A customer's routing rule: Window opens and Route by, in business days (CUST_REF1, CUST_REF2). */
export interface RoutingRule {
  openBdays: number | null;
  dueBdays: number | null;
}

export const NO_RULE: RoutingRule = { openBdays: null, dueBdays: null };

/** Where a rule in use came from (`routingRuleFor`). */
export type RuleFrom = "app" | "app-group" | "a2000" | "a2000-group" | "a2000-name" | "separate" | "none";

/** Where a separate code's own rule came from. */
export type RuleBasis = "app" | "a2000" | "a2000-name" | "none";

export interface RuleInUse extends RoutingRule {
  from: RuleFrom;
  /** A separate code's (`from` "separate"): its own rule's source, and the group it is kept apart from. */
  basis?: RuleBasis;
  group?: string;
  /** The codes it came from: its own A2000 code, a group's other codes, or the A2000 code borrowed by name. */
  fromCodes: string[];
  /** An app rule's: who saved it last, and when (YYYY-MM-DD). */
  setBy: string;
  setOn: string | null;
  /**
   * An app rule's: what A2000 would give without it (its own CUST_REF1/2, its group's, the borrow by
   * name), null for none — said as "not following A2000 (A2000 has 21 / 1)". Information only: the
   * rule chosen is the app's. Absent on any other rule.
   */
  a2000?: RoutingRule | null;
}

/** A stored day count as a number, or null (empty, unreadable). */
const days = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export const ruleOf = (r: { openBdays?: unknown; dueBdays?: unknown }): RoutingRule => ({
  openBdays: days(r.openBdays),
  dueBdays: days(r.dueBdays),
});

export const hasRule = (r: RoutingRule) => r.openBdays !== null || r.dueBdays !== null;

/** A stored flag (BIT): true, or 1. */
export const isFlag = (v: unknown) => v === true || Number(v) === 1;

/** What a `routing-rule` record holds besides its customer: the days and the two flags. */
export interface RuleValues extends RoutingRule {
  noRule: boolean;
  separate: boolean;
}

export const valuesOf = (r: Partial<RoutingRuleRecord> | null | undefined): RuleValues => ({
  ...ruleOf(r ?? {}),
  noRule: isFlag(r?.noRule),
  separate: isFlag(r?.separate),
});
export const sameRule = (a: RoutingRule, b: RoutingRule) => a.openBdays === b.openBdays && a.dueBdays === b.dueBdays;

/** The strictest of several rules: the larger open days and the larger due days among those set. */
export function strictestRule(rules: readonly RoutingRule[]): RoutingRule {
  const most = (xs: (number | null)[]) => {
    const set = xs.filter((x): x is number => x !== null);
    return set.length ? Math.max(...set) : null;
  };
  return { openBdays: most(rules.map((r) => r.openBdays)), dueBdays: most(rules.map((r) => r.dueBdays)) };
}

/** "3 / 2", "4 / —"; "None" without either. */
export const ruleText = (r: RoutingRule) => (hasRule(r) ? `${r.openBdays ?? "—"} / ${r.dueBdays ?? "—"}` : "None");

/** A day count as typed: whole, 0–60. Undefined and null are empty. */
export const isRuleDays = (v: unknown) =>
  v === null || v === undefined || (typeof v === "number" && Number.isInteger(v) && v >= 0 && v <= RULE_DAYS_MAX);

/** A typed day count: "" is empty; else a whole number 0–60. */
export function parseRuleDays(
  typed: string,
  label: string,
): { ok: true; value: number | null } | { ok: false; message: string } {
  const t = typed.trim();
  if (!t) return { ok: true, value: null };
  if (!/^\d+$/.test(t) || Number(t) > RULE_DAYS_MAX)
    return { ok: false, message: `${label}: a whole number of business days, 0–${RULE_DAYS_MAX} — not “${t}”.` };
  return { ok: true, value: Number(t) };
}

/** What a typed rule would do: refused (not 0–60), or saved with a warning (one side alone). */
export type RuleCheck =
  { ok: false; message: string } | { ok: true; rule: RoutingRule; warning: { title: string; text: string } | null };

export function checkRule(open: string, due: string): RuleCheck {
  const o = parseRuleDays(open, "Window opens");
  if (!o.ok) return o;
  const d = parseRuleDays(due, "Route by");
  if (!d.ok) return d;
  const rule = { openBdays: o.value, dueBdays: d.value };
  return {
    ok: true,
    rule,
    warning:
      rule.openBdays === null && rule.dueBdays !== null
        ? {
            title: "Route by without Window opens — are you sure?",
            text: "Without Window opens the line shows No routing rule: its window never opens.",
          }
        : rule.openBdays !== null && rule.dueBdays === null
          ? {
              title: "Window opens without Route by — are you sure?",
              text: "Lines will have no Route by date: their window opens, but nothing says when it is late.",
            }
          : null,
  };
}

// ---------------------------------------------------------------------------------------------
// What the rule reads

/** One A2000 customer's CUST_REF1 / CUST_REF2 (TRY_CAST to int: text there is no rule). */
export interface A2000Rule extends RoutingRule {
  code: string;
  name: string;
}

/**
 * A2000's rules the pages count with, built by the server from the whole open source and the
 * customer master (`vw_routing_customers`, when it has the columns): a page's subset keeps them.
 */
export interface RuleBase {
  /** Every A2000 code with a rule: the open pick tickets', then the customer master's. */
  a2000: A2000Rule[];
  /** The borrow by name (step 3): a name exactly one A2000 code with open pick tickets has, and its rule. */
  byName: A2000Rule[];
  /** Whether the customer master's CUST_REF1/2 were read; else only the open pick tickets'. */
  master: boolean;
}

/** An A2000 name as the borrow compares it: spaces and case aside (exactly the name otherwise). */
const exactName = (name: string) => name.trim().replace(/\s+/g, " ").toUpperCase();
const codeKey = (code: string) => code.trim().toUpperCase();

/** `RuleBase` from the A2000 source rows (Deposco's are skipped) and, when read, the customer master's rules. */
export function ruleBase(source: readonly SourceRow[], master: readonly A2000Rule[] | null = null): RuleBase {
  const a2000 = new Map<string, A2000Rule>();
  const names = new Map<string, { codes: Set<string>; rule: A2000Rule }>();
  for (const s of source) {
    if (s.deposco) continue;
    const code = String(s.customer_code ?? "").trim();
    const name = String(s.customer_name ?? "").trim();
    const rule = { code, name, openBdays: s.routing_open_bdays ?? null, dueBdays: s.routing_due_bdays ?? null };
    if (code && !a2000.has(codeKey(code)) && hasRule(rule)) a2000.set(codeKey(code), rule);
    if (!name) continue;
    const had = names.get(exactName(name));
    if (had) had.codes.add(codeKey(code));
    else names.set(exactName(name), { codes: new Set([codeKey(code)]), rule });
  }
  for (const m of master ?? []) {
    const code = m.code.trim();
    if (code && !a2000.has(codeKey(code)) && hasRule(m)) a2000.set(codeKey(code), { ...m, code });
  }
  return {
    a2000: [...a2000.values()],
    byName: [...names.values()].filter((n) => n.codes.size === 1 && hasRule(n.rule)).map((n) => n.rule),
    master: master !== null,
  };
}

/** A customer as the rule reads it; `a2000` its own CUST_REF1/2 as its row says it (a held copy's), else the base's. */
export interface RuleCustomer {
  source: LineSource;
  code: string;
  name: string;
  a2000?: RoutingRule | null;
}

/** Everything `routingRuleFor` reads, indexed once. */
export interface RuleContext {
  name: CustomerNamer;
  app: Map<string, RoutingRuleRecord>;
  appByShown: Map<string, RoutingRuleRecord[]>;
  a2000: Map<string, A2000Rule>;
  a2000ByShown: Map<string, A2000Rule[]>;
  byName: Map<string, A2000Rule>;
  /** The codes kept separate from their group (`separate`): neither in its app rules nor its A2000 ones. */
  separate: Set<string>;
}

const sourceOf = (r: { source?: unknown }): LineSource => (r.source === "Deposco" ? "Deposco" : "A2000");
const recordKey = (r: RoutingRuleRecord) => customerKey(sourceOf(r), String(r.code ?? ""));

export function ruleContext(input: {
  routingRules?: readonly RoutingRuleRecord[] | null;
  customerNames?: readonly CustomerNameRecord[] | null;
  base: RuleBase;
}): RuleContext {
  const name = customerNamer(input.customerNames ?? []);
  const push = <T>(m: Map<string, T[]>, k: string, v: T) => m.set(k, [...(m.get(k) ?? []), v]);
  const app = new Map<string, RoutingRuleRecord>();
  const appByShown = new Map<string, RoutingRuleRecord[]>();
  const separate = new Set<string>();
  for (const r of input.routingRules ?? []) {
    if (!String(r.code ?? "").trim()) continue;
    app.set(recordKey(r), r);
    if (isFlag(r.separate)) {
      separate.add(recordKey(r));
      continue;
    }
    push(
      appByShown,
      name({ code: String(r.code).trim(), name: String(r.customerName ?? ""), source: sourceOf(r) }).text,
      r,
    );
  }
  const a2000 = new Map<string, A2000Rule>();
  const a2000ByShown = new Map<string, A2000Rule[]>();
  for (const r of input.base.a2000) {
    a2000.set(codeKey(r.code), r);
    if (!separate.has(customerKey("A2000", r.code)))
      push(a2000ByShown, name({ code: r.code, name: r.name, source: "A2000" }).text, r);
  }
  return {
    name,
    app,
    appByShown,
    a2000,
    a2000ByShown,
    byName: new Map(input.base.byName.map((r) => [exactName(r.name), r])),
    separate,
  };
}

/** The codes whose own rule gave the result's open or due days. */
const contributors = <T extends RoutingRule>(rows: readonly T[], result: RoutingRule, codeOf: (r: T) => string) => [
  ...new Set(
    rows
      .filter(
        (r) =>
          (result.openBdays !== null && r.openBdays === result.openBdays) ||
          (result.dueBdays !== null && r.dueBdays === result.dueBdays),
      )
      .map(codeOf),
  ),
];

/** Which numbers a pick ticket's (SO's) customer counts with, and where they came from (the order above). */
export function routingRuleFor(c: RuleCustomer, ctx: RuleContext): RuleInUse {
  const code = c.code.trim();
  const key = code ? customerKey(c.source, code) : "";
  const shown = ctx.name({ code, name: c.name, source: c.source }).text;
  const none = { setBy: "", setOn: null };
  const setOf = (r: RoutingRuleRecord | undefined) => ({
    setBy: String(r?.updatedBy ?? ""),
    setOn: r?.updated_at ? String(r.updated_at).slice(0, 10) : null,
  });
  const own = key ? ctx.app.get(key) : undefined;
  const mine = c.source === "A2000" && code ? (c.a2000 ?? ctx.a2000.get(codeKey(code)) ?? null) : null;
  const borrowed = c.source === "Deposco" && c.name ? ctx.byName.get(exactName(c.name)) : undefined;

  // Kept separate from its group: its own only.
  if (own && isFlag(own.separate)) {
    const kept = { from: "separate" as const, group: shown };
    const alone = mine && hasRule(mine) ? ruleOf(mine) : borrowed ? ruleOf(borrowed) : null;
    if (isFlag(own.noRule)) return { ...NO_RULE, ...kept, basis: "app", fromCodes: [], ...setOf(own), a2000: alone };
    if (hasRule(ruleOf(own)))
      return { ...ruleOf(own), ...kept, basis: "app", fromCodes: [], ...setOf(own), a2000: alone };
    if (mine && hasRule(mine)) return { ...ruleOf(mine), ...kept, basis: "a2000", fromCodes: [code], ...none };
    if (borrowed) return { ...ruleOf(borrowed), ...kept, basis: "a2000-name", fromCodes: [borrowed.code], ...none };
    return { ...NO_RULE, ...kept, basis: "none", fromCodes: [], ...none };
  }

  // 2. A2000's CUST_REF1/2: its own code's and its group's A2000 codes'. 3. A Deposco customer: the A2000
  // customer with exactly its name (open pick tickets, one code). Also what an app rule says A2000 has.
  const a2000Steps = (): RuleInUse => {
    const others = (ctx.a2000ByShown.get(shown) ?? []).filter(
      (r) => c.source !== "A2000" || codeKey(r.code) !== codeKey(code),
    );
    const a2000 = [...(mine && hasRule(mine) ? [{ ...mine, code, name: c.name }] : []), ...others];
    if (a2000.length) {
      const result = strictestRule(a2000);
      const isOwn = !!mine && hasRule(mine) && sameRule(mine, result);
      return {
        ...result,
        from: isOwn ? "a2000" : "a2000-group",
        fromCodes: isOwn ? [code] : contributors(others, result, (r) => r.code),
        ...none,
      };
    }
    if (borrowed) return { ...ruleOf(borrowed), from: "a2000-name", fromCodes: [borrowed.code], ...none };
    return { ...NO_RULE, from: "none", fromCodes: [], ...none };
  };
  const a2000Has = () => {
    const r = a2000Steps();
    return r.from === "none" ? null : ruleOf(r);
  };

  // 1. The app's rules: its own and its group's records. Days beat No rule; No rule only when it is all there is.
  const group = (ctx.appByShown.get(shown) ?? []).filter((r) => recordKey(r) !== key);
  const rules = [...(own ? [own] : []), ...group];
  const withDays = rules.filter((r) => !isFlag(r.noRule) && hasRule(ruleOf(r)));
  if (withDays.length) {
    const result = strictestRule(withDays.map(ruleOf));
    const latest = [...withDays].sort((a, b) =>
      String(b.updated_at ?? "").localeCompare(String(a.updated_at ?? "")),
    )[0];
    const isOwn = !!own && withDays.includes(own) && sameRule(ruleOf(own), result);
    return {
      ...result,
      from: isOwn ? "app" : "app-group",
      fromCodes: isOwn
        ? []
        : contributors(
            withDays.filter((r) => r !== own),
            result,
            (r) => String(r.code),
          ),
      ...setOf(isOwn ? own : latest),
      a2000: a2000Has(),
    };
  }
  const noRules = rules.filter((r) => isFlag(r.noRule));
  if (noRules.length) {
    const isOwn = !!own && isFlag(own.noRule);
    return {
      ...NO_RULE,
      from: isOwn ? "app" : "app-group",
      fromCodes: isOwn ? [] : noRules.map((r) => String(r.code)),
      ...setOf(isOwn ? own : noRules[0]),
      a2000: a2000Has(),
    };
  }

  return a2000Steps();
}

/** A pick ticket's rule as the line's window reads it: the numbers, and where they came from. */
export type RuleSource = Pick<RuleInUse, "from" | "fromCodes" | "setBy" | "setOn" | "basis" | "group" | "a2000">;

/** A window's days: "10 / 3", "— / 3"; none: "No window". */
export const windowDays = (r: RoutingRule) => (hasRule(r) ? ruleText(r) : "No window");

/** "code BEALLS", "codes BEALLS, BEALLSOUTL". */
const codesWord = (codes: readonly string[]) => `${codes.length === 1 ? "code" : "codes"} ${codes.join(", ")}`;

/** Set in the app: its own record, its group's, or a separate code's own. */
const isAppRule = (r: Pick<RuleInUse, "from" | "basis">) =>
  r.from === "app" || r.from === "app-group" || (r.from === "separate" && r.basis === "app");

/**
 * Where a window comes from, in plain words — the one wording of Admin → Routing rules' Window in
 * use, the Worklist's hover and `rule_source`: "Set in the app", "Set in the app for BEALLS", "From
 * A2000 (this customer)", "From A2000 · code BEALLS in this group", "From A2000 · same name as WMT",
 * "Its own window: kept separate from BEALLS", "Turned off in the app", "No window in A2000 or the
 * app". Without days (`openBdays`/`dueBdays` undefined) a rule counts as giving a window. `inGroup`: said
 * of a group's row, where "this customer" would be wrong — its own A2000 code is named.
 */
export function windowSource(r: RuleSource & Partial<RoutingRule>, opts: { inGroup?: boolean } = {}): string {
  const off = r.openBdays === null && r.dueBdays === null;
  const codes = r.fromCodes;
  const app = (s: string) => (off ? `Turned off in the app${s}` : `Set in the app${s}`);
  switch (r.from) {
    case "app":
      return app("");
    case "app-group":
      return app(codes.length ? ` for ${codes.join(", ")}` : " for this group");
    case "a2000":
      return opts.inGroup ? `From A2000 · ${codesWord(codes)} in this group` : "From A2000 (this customer)";
    case "a2000-group":
      return `From A2000 · ${codesWord(codes)} in this group`;
    case "a2000-name":
      return `From A2000 · same name as ${codes.join(", ")}`;
    case "separate": {
      const from = ` from ${r.group ?? "its group"}`;
      if (r.basis === "app")
        return off ? `Turned off in the app · kept separate${from}` : `Its own window: kept separate${from}`;
      const own =
        r.basis === "a2000"
          ? "From A2000 (this customer)"
          : r.basis === "a2000-name"
            ? `From A2000 · same name as ${codes.join(", ")}`
            : "No window in A2000 or the app";
      return `${own} · kept separate${from}`;
    }
    default:
      return "No window in A2000 or the app";
  }
}

/**
 * An app rule's end of line 2 (Admin's Window in use, the Worklist's hover): it no longer follows A2000,
 * and what A2000 has — " · not following A2000 (A2000 has 21 / 1)", "(A2000 has none)"; "" for any other.
 * `mixed`: a group whose codes' A2000 days differ.
 */
export function followText(r: Pick<RuleInUse, "from" | "basis" | "a2000">, mixed = false): string {
  if (!isAppRule(r)) return "";
  const has = mixed ? "different days per code" : r.a2000 && hasRule(r.a2000) ? ruleText(r.a2000) : "none";
  return ` · not following A2000 (A2000 has ${has})`;
}

/**
 * Where a window's date came from, for the Worklist's hover: `windowSource`'s words, who set an app rule,
 * and what A2000 has then — "Set in the app, set by Dijana Icov 10/30 · not following A2000 (A2000 has
 * 21 / 1)", "From A2000 · code BLK in this group".
 */
export function ruleSourceText(r: RuleSource & Partial<RoutingRule>): string {
  const set = [r.setBy ? `set by ${r.setBy}` : "", r.setOn ? formatShortDay(r.setOn) : ""].filter(Boolean).join(" ");
  return `${windowSource(r)}${isAppRule(r) && set ? `, ${set}` : ""}${followText(r)}`;
}

/** A line's rule source: the pick ticket's that set Window opens, else Route by's; none without either. */
export const windowRuleFrom = (w: {
  opensBy: { rule?: RuleSource } | null;
  routeBySetBy: { rule?: RuleSource } | null;
}) => w.opensBy?.rule?.from ?? w.routeBySetBy?.rule?.from ?? "none";

/**
 * A line's rule as the reports and Ask AI say it (`rule_source`): `windowSource`'s words and the
 * days — "From A2000 · code BEALLS in this group · 10 / 3", "Set in the app · 3 / 2"; a line with
 * no window: "No window" (its pick tickets' rules are not on the window: turned off or none alike).
 */
export function ruleSourceLabel(w: {
  opensBy: { rule?: RuleSource } | null;
  routeBySetBy: { rule?: RuleSource } | null;
  openBdays: number | null;
  dueBdays: number | null;
}): string {
  const rule = w.opensBy?.rule ?? w.routeBySetBy?.rule;
  if (!rule || windowRuleFrom(w) === "none") return "No window";
  return `${windowSource(rule)} · ${ruleText(w)}`;
}

/** A rule in use in one line: "10 / 3 · From A2000 (this customer)", "No window · Turned off in the app", "No window in A2000 or the app". */
export const inUseText = (r: RuleInUse) => {
  const source = windowSource(r);
  return hasRule(r) ? `${ruleText(r)} · ${source}` : r.from === "none" ? source : `No window · ${source}`;
};

// ---------------------------------------------------------------------------------------------
// Admin → Routing rules

/** One code of an Admin row. */
export interface RuleCodeRow {
  key: string;
  source: LineSource;
  code: string;
  name: string;
  openLines: number;
  active: boolean;
  /** Its own CUST_REF1/2 (A2000 only); null without one. */
  a2000: RoutingRule | null;
  /** Its app record; null without one. */
  record: RoutingRuleRecord | null;
  /** Kept separate from its group: its own In use, out of the group's. */
  separate: boolean;
  inUse: RuleInUse;
  /** What it counts with alone (`aloneRule`): its own app days, its own A2000, the borrow, none. */
  alone: RuleInUse;
}

/** One row of Admin → Routing rules: a shown name (customers sharing one are one row) and its codes. */
export interface RoutingRuleRow {
  shown: string;
  codes: RuleCodeRow[];
  openLines: number;
  /** Its A2000 codes' rule: one, none, or several that differ (`mixed`, each on hover). */
  a2000: { rule: RoutingRule | null; mixed: boolean };
  /** How many of its codes are kept separate; the group's own columns are the other codes'. */
  separate: number;
  /** Its merged codes' app records: none (`rule` null), one rule for all, or several that differ (`mixed`). */
  app: {
    rule: RoutingRule | null;
    mixed: boolean;
    /** Every merged code's record says No routing rule. */
    noRule: boolean;
    records: number;
    notes: string;
    updatedBy: string;
    updatedOn: string | null;
  };
  /** The rule its merged codes count with (every code when all are separate): one, or several (`mixed`). */
  inUse: { rule: RuleInUse | null; mixed: boolean; kind: RuleKind };
  /** A code with open lines that has no rule: the list starts with these. */
  needsRule: boolean;
}

/** In use, as the filter says it. */
export type RuleKind = "none" | "app" | "a2000";

const kindOf = (r: RuleInUse): RuleKind =>
  r.openBdays === null
    ? "none"
    : r.from === "app" || r.from === "app-group" || (r.from === "separate" && r.basis === "app")
      ? "app"
      : "a2000";

/** A record that sets something for the group: days or No rule (not one kept only for `separate`). */
const setsRule = (r: RoutingRuleRecord) => isFlag(r.noRule) || hasRule(ruleOf(r));

/** Rules that are all the same give it; else `mixed`. */
function oneOf<T extends RoutingRule>(rules: readonly T[]): { rule: T | null; mixed: boolean } {
  if (!rules.length) return { rule: null, mixed: false };
  const mixed = rules.some((r) => !sameRule(r, rules[0]!));
  return { rule: mixed ? null : rules[0]!, mixed };
}

/** A group: a shown name over two or more codes (an arrow, its codes under it); one code is a single customer. */
export const isRuleGroup = (r: Pick<RoutingRuleRow, "codes">) => r.codes.length > 1;

/**
 * A group row's Window in use, line 2, while one of its boxes is focused: which codes a save there
 * changes — "Typing here saves on all 5 codes", "Typing here saves on 4 codes (1 kept separate)".
 */
export const groupSaveText = (r: Pick<RoutingRuleRow, "codes">) => {
  const apart = r.codes.filter((c) => c.separate).length;
  const on = r.codes.length - apart;
  return apart
    ? `Typing here saves on ${plural(on, "code")} (${apart} kept separate)`
    : `Typing here saves on all ${plural(on, "code")}`;
};

/** A group's size under its name: "5 codes", "5 codes · 1 kept separate". */
export const groupSizeText = (r: Pick<RoutingRuleRow, "codes" | "separate">) =>
  `${r.codes.length.toLocaleString("en-US")} codes${r.separate ? ` · ${r.separate} kept separate` : ""}`;

/** Groups first, then single customers; in each, needing a rule first, then open lines (most first), then the name. */
export const compareRuleRows = (a: RoutingRuleRow, b: RoutingRuleRow) =>
  Number(isRuleGroup(b)) - Number(isRuleGroup(a)) ||
  Number(b.needsRule) - Number(a.needsRule) ||
  b.openLines - a.openLines ||
  nameKey(a.shown).localeCompare(nameKey(b.shown)) ||
  a.shown.localeCompare(b.shown);

/** A customer the Admin lists (`customerOptions`). */
export interface RuleCustomerOption {
  key: string;
  source: LineSource;
  code: string;
  name: string;
  openLines: number;
  active: boolean;
}

/** The Admin list: every customer of `options` and every one with a record, one row per shown name. */
export function routingRuleRows(
  options: readonly RuleCustomerOption[],
  input: {
    routingRules: readonly RoutingRuleRecord[];
    customerNames: readonly CustomerNameRecord[];
    base: RuleBase;
  },
): RoutingRuleRow[] {
  const ctx = ruleContext(input);
  const customers = new Map(options.map((o) => [o.key, o]));
  for (const r of input.routingRules) {
    const key = recordKey(r);
    if (!customers.has(key) && String(r.code ?? "").trim())
      customers.set(key, {
        key,
        source: sourceOf(r),
        code: String(r.code).trim(),
        name: String(r.customerName ?? ""),
        openLines: 0,
        active: false,
      });
  }
  const groups = new Map<string, RuleCodeRow[]>();
  for (const c of customers.values()) {
    const a2000 = c.source === "A2000" ? (ctx.a2000.get(codeKey(c.code)) ?? null) : null;
    const row: RuleCodeRow = {
      ...c,
      a2000: a2000 ? ruleOf(a2000) : null,
      record: ctx.app.get(c.key) ?? null,
      separate: ctx.separate.has(c.key),
      inUse: routingRuleFor(c, ctx),
      alone: aloneRule(c, ctx),
    };
    const shown = ctx.name(c).text;
    groups.set(shown, [...(groups.get(shown) ?? []), row]);
  }
  return [...groups]
    .map(([shown, codes]): RoutingRuleRow => {
      codes.sort(
        (a, b) =>
          b.openLines - a.openLines ||
          (a.source === b.source ? 0 : a.source === "A2000" ? -1 : 1) ||
          a.code.localeCompare(b.code),
      );
      const merged = codes.filter((c) => !c.separate);
      const records = merged.flatMap((c) => (c.record && setsRule(c.record) ? [c.record] : []));
      const latest = [...records].sort((a, b) =>
        String(b.updated_at ?? "").localeCompare(String(a.updated_at ?? "")),
      )[0];
      const counted = merged.length ? merged : codes;
      const inUse = oneOf(counted.map((c) => c.inUse));
      const kinds = new Set(counted.map((c) => kindOf(c.inUse)));
      return {
        shown,
        codes,
        openLines: codes.reduce((n, c) => n + c.openLines, 0),
        a2000: oneOf(merged.flatMap((c) => (c.a2000 ? [c.a2000] : []))),
        separate: codes.length - merged.length,
        app: {
          ...oneOf(records.map(ruleOf)),
          noRule: records.length > 0 && records.every((r) => isFlag(r.noRule)),
          records: records.length,
          notes: [...new Set(records.map((r) => String(r.notes ?? "").trim()).filter(Boolean))].join(" · "),
          updatedBy: String(latest?.updatedBy ?? ""),
          updatedOn: latest?.updated_at ? String(latest.updated_at).slice(0, 10) : null,
        },
        inUse: {
          ...inUse,
          kind: kinds.has("app") ? "app" : kinds.has("a2000") ? "a2000" : "none",
        },
        needsRule: codes.some((c) => c.openLines > 0 && c.inUse.openBdays === null),
      };
    })
    .sort(compareRuleRows);
}

/** The Admin tab's filters. */
export interface RoutingRuleFilters {
  search: string;
  source: "all" | LineSource;
  withOpenLines: boolean;
  /** Window in use: any, none, from A2000, set in the app, or turned off in the app (a part of none). */
  inUse: "all" | RuleKind | "off";
}

export const DEFAULT_ROUTING_RULE_FILTERS: RoutingRuleFilters = {
  search: "",
  source: "all",
  withOpenLines: true,
  inUse: "all",
};

/** What a search matches on a row: its shown name, else one of its codes (the code or its original name), else none. */
export function ruleRowMatch(r: Pick<RoutingRuleRow, "shown" | "codes">, search: string): "shown" | "code" | null {
  const words = search.trim().toLowerCase();
  if (!words || r.shown.toLowerCase().includes(words)) return "shown";
  return r.codes.some((c) => `${c.code} ${c.name}`.toLowerCase().includes(words)) ? "code" : null;
}

/** A search that matches a code inside a group opens that group, so the code shows; a name match does not. */
export const opensOnSearch = (r: Pick<RoutingRuleRow, "shown" | "codes">, search: string) =>
  !!search.trim() && isRuleGroup(r) && ruleRowMatch(r, search) === "code";

export const filterRuleRows = (rows: readonly RoutingRuleRow[], f: RoutingRuleFilters) =>
  rows.filter(
    (r) =>
      (f.source === "all" || r.codes.some((c) => c.source === f.source)) &&
      (!f.withOpenLines || r.openLines > 0) &&
      (f.inUse === "all" ||
        (f.inUse === "none"
          ? r.needsRule || r.inUse.kind === "none"
          : f.inUse === "off"
            ? r.app.noRule
            : r.inUse.kind === f.inUse)) &&
      ruleRowMatch(r, f.search) !== null,
  );

/** The Window in use buttons, in order, each with how many rows the other filters keep under it. */
export const RULE_IN_USE_FILTERS = [
  { id: "all", label: "All" },
  { id: "none", label: "No window" },
  { id: "a2000", label: "From A2000" },
  { id: "app", label: "Set in the app" },
  { id: "off", label: "Turned off" },
] as const satisfies readonly { id: RoutingRuleFilters["inUse"]; label: string }[];

export const ruleFilterCounts = (rows: readonly RoutingRuleRow[], f: RoutingRuleFilters) =>
  Object.fromEntries(
    RULE_IN_USE_FILTERS.map((b) => [b.id, filterRuleRows(rows, { ...f, inUse: b.id }).length]),
  ) as Record<RoutingRuleFilters["inUse"], number>;

/** "1 open line", "1,296 open lines". */
const plural = (n: number, one: string) => `${n.toLocaleString("en-US")} ${n === 1 ? one : `${one}s`}`;

/** Admin → Routing rules' Window in use: the days, where they come from, and A2000's where an app rule overrides it. */
export interface WindowCell {
  /** Line 1, bold: "10 / 3", "No window"; a group whose codes count differently: "Differs by code". */
  days: string;
  mixed: boolean;
  /** No Window opens: the lines show No routing rule (Route by alone too: the window never opens). */
  off: boolean;
  routeByAlone: boolean;
  /** Line 1's hover: the days in words (each code's, when they differ). */
  hover: string;
  /** Line 2: `windowSource`'s words. */
  source: string;
  /**
   * Line 2's end on an app rule (`followText`): " · not following A2000 (A2000 has 21 / 1)", "(A2000 has
   * none)"; "" on any other. Always said, so a copied rule shows it no longer follows A2000.
   */
  follow: string;
}

/** Line 2 of a row with no window: "No window" is line 1 already, so not said twice. */
export const NOT_IN_EITHER = "Not in A2000 or the app";

function cellOf(r: RuleInUse, source: string, a2000Mixed = false): WindowCell {
  return {
    days: windowDays(r),
    mixed: false,
    off: r.openBdays === null,
    routeByAlone: r.openBdays === null && r.dueBdays !== null,
    hover: hasRule(r) ? ruleWords(r) : "No routing window: the lines show No routing rule",
    source: source.replace("No window in A2000 or the app", NOT_IN_EITHER),
    follow: followText(r, a2000Mixed),
  };
}

/** One code's Window in use (a single customer's row, a group's code line). */
export const codeWindowCell = (c: RuleCodeRow): WindowCell => cellOf(c.inUse, windowSource(c.inUse));

/**
 * A row's Window in use. A group's is its merged codes' (every code when all are separate), said by
 * the code that wins: "Set in the app for BLK", "From A2000 · code BEALLS in this group".
 */
export function rowWindowCell(row: RoutingRuleRow): WindowCell {
  if (!isRuleGroup(row)) return codeWindowCell(row.codes[0]!);
  const counted = row.separate < row.codes.length ? row.codes.filter((c) => !c.separate) : row.codes;
  const rule = row.inUse.rule;
  if (row.inUse.mixed || !rule)
    return {
      days: "Differs by code",
      mixed: true,
      off: false,
      routeByAlone: false,
      hover: counted.map((c) => `${c.code}: ${inUseText(c.inUse)}`).join("\n"),
      source: "Open the group to see each code's",
      follow: "",
    };
  const winner =
    counted.find((c) => (c.inUse.from === "app" || c.inUse.from === "a2000") && sameRule(c.inUse, rule)) ?? counted[0]!;
  const w = winner.inUse;
  const source =
    w.from === "app" && !counted.every((c) => c.inUse.from === "app")
      ? windowSource({ ...w, from: "app-group", fromCodes: [winner.code] })
      : windowSource(w, { inGroup: true });
  return cellOf(w, source, row.a2000.mixed);
}

/** A window that comes from A2000: the code's own, its group's, borrowed by name, or a separate code's own A2000. */
const fromA2000 = (r: Pick<RuleInUse, "from" | "basis">) =>
  r.from === "a2000" ||
  r.from === "a2000-group" ||
  r.from === "a2000-name" ||
  (r.from === "separate" && (r.basis === "a2000" || r.basis === "a2000-name"));

/** A row with a window to turn off: one in use with days (A2000's, a group's, a borrow, the app's), or codes that differ. */
export const rowHasWindow = (row: Pick<RoutingRuleRow, "inUse">) =>
  row.inUse.mixed || (!!row.inUse.rule && hasRule(row.inUse.rule));

/** "Copy A2000's days" on a row: the days to save, or none with the reason; its hover either way. */
export interface CopyOffer {
  rule: RoutingRule | null;
  hover: string;
}

/**
 * One rule for every row, single or group: offered when the window in use comes from A2000 (its own,
 * its group's or borrowed by name) — it saves those days in the app on the row's merged codes. A
 * group whose codes have different A2000 days says so: the days in use are the strictest.
 */
export function copyOffer(row: RoutingRuleRow): CopyOffer {
  const r = row.inUse.rule;
  if (row.inUse.mixed || !r)
    return { rule: null, hover: `${row.shown}'s codes count with different windows: set the days you want` };
  if (!hasRule(r) || !fromA2000(r))
    return {
      rule: null,
      hover: isAppRule(r) ? "The window is set in the app already" : `No A2000 days for ${row.shown}`,
    };
  const days = ruleOf(r);
  const group = isRuleGroup(row);
  if (group && row.a2000.mixed) {
    const each = [
      ...new Set(row.codes.flatMap((c) => (!c.separate && c.a2000 && hasRule(c.a2000) ? [ruleText(c.a2000)] : []))),
    ];
    return {
      rule: days,
      hover: `${row.shown}'s codes have different A2000 days (${each.join(", ")}): the days in use are the strictest. ${copyHover(days, "group")}`,
    };
  }
  return { rule: days, hover: copyHover(days, group ? "group" : "customer") };
}

/**
 * Copy A2000's days' hover: what it saves and what is given up — A2000's later changes no longer count
 * here, until Use A2000's: "Saves 21 / 1 as the app's rule. From then on, changes to this customer's
 * CUST_REF in A2000 won't change its window here, until you click Use A2000's."
 */
export const copyHover = (days: RoutingRule, of: "customer" | "code" | "group") =>
  `Saves ${ruleText(days)} as the app's rule. From then on, changes to ${of === "group" ? "the group's codes'" : `this ${of}'s`} CUST_REF in A2000 won't change ${of === "group" ? "their" : "its"} window here, until you click Use A2000's.`;

/** The bar's Copy: the same, for each selected customer. */
export const BULK_COPY_HOVER =
  "Saves each selected customer's A2000 days as the app's rule. From then on, changes to their CUST_REF in A2000 won't change their window here, until you click Use A2000's.";

/** Copy's toast: "DDSDISCOUN: 21 / 1 set in the app (no longer follows A2000)"; a group's names how many codes. */
export const copiedText = (shown: string, days: RoutingRule, codes = 1) =>
  `${shown}: ${ruleText(days)} set in the app${codes > 1 ? ` on ${plural(codes, "code")}` : ""} (no longer ${codes > 1 ? "follow" : "follows"} A2000)`;

/** The bar's Copy on several: "A2000's days set in the app on 3 customers (they no longer follow A2000)". */
export const bulkCopiedText = (customers: number) =>
  `A2000's days set in the app on ${plural(customers, "customer")} (${customers === 1 ? "it no longer follows" : "they no longer follow"} A2000)`;

/**
 * The bar's Copy asks as a row's does (`checkRule`) when A2000 has one side only: the ticked rows it
 * would copy with Window opens or Route by missing, "BELK 4 / —"; null when none has.
 */
export function bulkCopyQuestion(
  rows: readonly RoutingRuleRow[],
): { title: string; text: string; lines: string[] } | null {
  const lines = rows.flatMap((r) => {
    const days = copyOffer(r).rule;
    return days && (days.openBdays === null) !== (days.dueBdays === null) ? [`${r.shown} ${ruleText(days)}`] : [];
  });
  if (!lines.length) return null;
  return {
    title: `A2000 has one side only for ${plural(lines.length, "customer")} — copy anyway?`,
    text: "Without Window opens a line's window never opens; without Route by nothing says when it is late. The others are copied as they are.",
    lines,
  };
}

/** A code's own "Copy A2000's days": only a separate code has its own rule; in its group, the group's row has it. */
export function codeCopyOffer(c: RuleCodeRow): CopyOffer {
  if (!c.separate) return { rule: null, hover: "In its group: the group's row sets the days" };
  const r = c.inUse;
  if (!hasRule(r) || !fromA2000(r))
    return {
      rule: null,
      hover: isAppRule(r) ? "The window is set in the app already" : `No A2000 days for ${c.code}`,
    };
  return {
    rule: ruleOf(r),
    hover: copyHover(ruleOf(r), "code"),
  };
}

/** The shown rows' open lines, beside the filter buttons (which give the row counts): "3,021 open lines". */
export const openLinesText = (rows: readonly Pick<RoutingRuleRow, "openLines">[]) =>
  plural(
    rows.reduce((n, r) => n + r.openLines, 0),
    "open line",
  );

/**
 * The bar's Keep separate and Join group: how many ticked codes each applies to — Keep separate the
 * codes of a group not kept separate, Join group the codes kept separate — and why none when none does.
 */
export function separateCounts(rows: readonly RoutingRuleRow[]): {
  keep: number;
  join: number;
  why: { keep: string; join: string };
} {
  return {
    keep: rows.filter(isRuleGroup).reduce((n, r) => n + r.codes.filter((c) => !c.separate).length, 0),
    join: rows.reduce((n, r) => n + r.codes.filter((c) => c.separate).length, 0),
    why: {
      keep: "None of the selected customers has a code in a group that isn't kept separate already",
      join: "None of the selected customers has a code kept separate",
    },
  };
}

/** The bar's buttons: how many ticked rows each applies to, and why none when none does. */
export function bulkCounts(rows: readonly RoutingRuleRow[]): {
  copy: number;
  reset: number;
  turnOff: number;
  why: { copy: string; reset: string; turnOff: string };
} {
  return {
    copy: rows.filter((r) => copyOffer(r).rule).length,
    reset: rows.filter((r) => r.app.records > 0).length,
    turnOff: rows.filter(rowHasWindow).length,
    why: {
      copy: "None of the selected customers has a window from A2000 that isn't set in the app",
      reset: "None of the selected customers has a rule set in the app",
      turnOff: "None of the selected customers has a window to turn off",
    },
  };
}

/**
 * A box typed into while the other is empty: the other gets the window in use's days for its side,
 * shown as a normal value, so the save keeps them; none for that side, it stays empty (and the save
 * asks first: a window with one side only). Only at the start of an edit.
 */
export function fillOtherSide<T extends { open: string; due: string }>(
  next: T,
  side: "open" | "due",
  inUse: RoutingRule | null,
): T {
  const other = side === "open" ? "due" : "open";
  if (next[other].trim() || !inUse) return next;
  const value = other === "open" ? inUse.openBdays : inUse.dueBdays;
  return value === null ? next : { ...next, [other]: String(value) };
}

/** The ticked rows' windows in use, side by side: a side's days when every row has the same, else null. */
export function sharedInUse(rows: readonly RoutingRuleRow[]): RoutingRule | null {
  const rules = rows.map((r) => (r.inUse.mixed ? null : r.inUse.rule));
  if (!rules.length || rules.some((r) => !r)) return null;
  const same = (k: "openBdays" | "dueBdays") => (rules.every((r) => r![k] === rules[0]![k]) ? rules[0]![k] : null);
  return { openBdays: same("openBdays"), dueBdays: same("dueBdays") };
}

/** A code's window alone, said by where it comes from (the release review's words): "7 / 1 · From A2000 (this customer)". */
export const aloneText = (r: RuleInUse) =>
  inUseText({ ...r, from: r.basis === "a2000" || r.basis === "a2000-name" || r.basis === "app" ? r.basis : "none" });

/** An opened group's code line: "Alone: 7 / 1 · From A2000 (this customer)" — what it would count with kept separate. */
export const aloneLabel = (c: Pick<RuleCodeRow, "alone">) => `Alone: ${aloneText(c.alone)}`;

/** A search that matches this code (its code or original name): its line is marked in an opened group. */
export const codeMatchesSearch = (c: Pick<RuleCodeRow, "code" | "name">, search: string) =>
  !!search.trim() && `${c.code} ${c.name}`.toLowerCase().includes(search.trim().toLowerCase());

/**
 * Every customer's window once `keys`' records are `to(had)` (null: emptied, as Reset) — what a button
 * leads to, counted by `routingRuleFor` itself, never guessed.
 */
export function windowsAfter(
  input: RuleInput,
  keys: readonly string[],
  to: (had: RuleValues) => RuleValues | null,
): Map<string, RuleInUse> {
  const set = new Set(keys);
  const added = input.customers.flatMap((c): RoutingRuleRecord[] => {
    if (!set.has(c.key)) return [];
    const had = input.routingRules.find((r) => recordKey(r) === c.key);
    const v = to(valuesOf(had));
    if (!v) return [];
    const base = had ?? { id: "", source: c.source, code: c.code, customerName: c.name, notes: "", updatedBy: "" };
    return [{ ...base, openBdays: v.openBdays, dueBdays: v.dueBdays, noRule: v.noRule, separate: v.separate }];
  });
  const ctx = ruleContext({
    ...input,
    routingRules: [...input.routingRules.filter((r) => !set.has(recordKey(r))), ...added],
  });
  return new Map(input.customers.map((c) => [c.key, routingRuleFor(c, ctx)]));
}

/** "Turn off window"'s "Are you sure?": its lines, or the codes whose app days still count for the group. */
export interface TurnOffPrompt {
  title: string;
  text: string;
  confirmLabel: string;
  /** The codes of a group whose app days still count once these are turned off: it would not work. */
  stillOn: string[];
}

/** Turn off window on `rows` (each its codes saved No routing rule): what to ask first. */
export function turnOffPrompt(
  input: RuleInput,
  rows: readonly { shown: string; keys: readonly string[]; openLines: number }[],
): TurnOffPrompt {
  const keys = rows.flatMap((r) => r.keys);
  const after = windowsAfter(input, keys, (had) => ({ ...had, ...NO_RULE, noRule: true }));
  const stillOn = [
    ...new Set(
      keys.flatMap((k) => {
        const w = after.get(k);
        return w && w.openBdays !== null ? w.fromCodes : [];
      }),
    ),
  ];
  const lines = rows.reduce((n, r) => n + r.openLines, 0);
  const one = rows.length === 1;
  const its = one ? "Its" : "Their";
  return {
    title: `Turn off the routing window for ${one ? rows[0]!.shown : plural(rows.length, "customer")}?`,
    text: stillOn.length
      ? `${stillOn.join(", ")} ${stillOn.length === 1 ? "has" : "have"} days set in the app, so they still count for the group. ${its} ${plural(lines, "open line")} ${lines === 1 ? "keeps" : "keep"} that window.`
      : `${its} ${plural(lines, "open line")} will show "No routing rule" until you set days or use A2000's again.`,
    confirmLabel: stillOn.length ? "Save anyway" : "Turn off window",
    stillOn,
  };
}

/** "Use A2000's" (Reset: `keys`' records emptied): its label and what the window becomes, on hover. */
export function resetResult(input: RuleInput, keys: readonly string[]): { label: string; hover: string } {
  const w = keys.length ? windowsAfter(input, keys, () => null).get(keys[0]!) : undefined;
  if (w?.from === "app-group")
    return {
      label: "Use the group's",
      hover: `Remove the app's rule. The window comes from the group again: ${windowDays(w)} (${windowSource(w)})`,
    };
  return { label: "Use A2000's", hover: `Follow A2000 again: today ${w && hasRule(w) ? ruleText(w) : "no window"}` };
}

const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

/**
 * A save's toast: "STITCH FIX, INC.: window 5 / 2, set in the app", "BEALLS: no window, turned off in the app";
 * a group's row saved on its codes names how many, "BEALLS: window 5 / 3 set in the app on 5 codes".
 */
export function savedWindowText(shown: string, cell: WindowCell, codes = 1): string {
  if (cell.mixed) return `${shown}: the codes' windows differ`;
  if (codes > 1 && cell.source === "Set in the app")
    return `${shown}: ${cell.off && !cell.routeByAlone ? "no window" : `window ${cell.days}`} set in the app on ${plural(codes, "code")}`;
  if (codes > 1 && cell.source === "Turned off in the app")
    return `${shown}: no window, turned off in the app on ${plural(codes, "code")}`;
  if (cell.off && cell.source.startsWith(NOT_IN_EITHER)) return `${shown}: no window in A2000 or the app`;
  return `${shown}: ${cell.off && !cell.routeByAlone ? "no window" : `window ${cell.days}`}, ${lowerFirst(cell.source)}`;
}

/** The change entry's line text: "Routing rule A2000 BLK · BELK INC.". */
export const routingRuleText = (c: Pick<RuleCodeRow, "source" | "code" | "name">) =>
  `Routing rule ${c.source} ${c.code}${c.name ? ` · ${c.name}` : ""}`;

/** `GET /api/routing/admin/routing-rules`: the Admin tab's list (the Admin feature only). */
export interface RoutingRulesPayload {
  rows: RoutingRuleRow[];
  /** What Keep separate's check reads (`leftBehind`): the records, the shown names and A2000's rules. */
  routingRules: RoutingRuleRecord[];
  customerNames: CustomerNameRecord[];
  ruleBase: RuleBase;
  /** The release review: the groups whose window changed when routing rules were released, not reviewed yet. */
  review: ReviewGroup[];
  /** Whether A2000's rules came from the customer master too (else only from the open pick tickets). */
  master: boolean;
}

// ---------------------------------------------------------------------------------------------
// Admin → Customers: what a shown name's save does to the routing windows

/** A customer whose window a save changes: its rule before and after. */
export interface RuleShift {
  key: string;
  source: LineSource;
  code: string;
  name: string;
  before: RuleInUse;
  after: RuleInUse;
}

/** A code "Keep their own routing windows" saves: kept separate; `pinned` — its window came from its old group, kept as its own days. */
export interface KeepOwn {
  key: string;
  source: LineSource;
  code: string;
  name: string;
  values: RuleValues;
  pinned: boolean;
}

/** The codes left in a group a code leaves, when the group's window changes for them. */
export interface GroupShift {
  /** The group's shown name, the codes left in it, and those leaving. */
  shown: string;
  codes: string[];
  leaving: string[];
  /** The group's window before and after: one for all, or null when its codes differ (Mixed). */
  before: RoutingRule | null;
  after: RoutingRule | null;
  /** "BELK (BLK, C005314, 55182) will go from 7 / 3 to 3 / 3 because BELKOUTL leaves." */
  text: string;
}

export interface RenameImpact {
  /** The shown name the saved codes take, and every code under it after the save (two or more), each before → after. */
  joined: { shown: string; members: RuleShift[]; strictest: RoutingRule } | null;
  /** Every code whose window the save changes. */
  shifts: RuleShift[];
  /** The saved codes whose own window changes as they leave a group whose window they used. */
  leaving: RuleShift[];
  /** The groups the saved codes leave, whose window changes for the codes left in them: a warning only. */
  leftBehind: GroupShift[];
  /** What "Keep their own routing windows" would save (`keepOwnPlan`). */
  keepOwn: KeepOwn[];
  /**
   * What to ask first: `share` — the joined codes' windows differ; `leave` — a saved code's window
   * changes as it leaves its group; `warn` — only a group left behind changes; `none`.
   */
  dialog: "share" | "leave" | "warn" | "none";
}

type RuleInput = {
  customers: readonly Pick<RuleCustomerOption, "key" | "source" | "code" | "name">[];
  customerNames: readonly CustomerNameRecord[];
  routingRules: readonly RoutingRuleRecord[];
  base: RuleBase;
};

/** The shown names with `changes` saved ("" is the default). */
function renamed(input: RuleInput, changes: readonly { key: string; shownName: string }[]): CustomerNameRecord[] {
  const byKey = new Map(input.customers.map((c) => [c.key, c]));
  const names = new Map(input.customerNames.map((r) => [customerKey(sourceOf(r), String(r.code ?? "")), r] as const));
  for (const ch of changes) {
    const c = byKey.get(ch.key);
    if (!c) continue;
    names.set(ch.key, {
      ...(names.get(ch.key) ?? { id: "", updatedBy: "" }),
      source: c.source,
      code: c.code,
      customerName: c.name,
      shownName: ch.shownName,
    });
  }
  return [...names.values()];
}

/** The records with `separate` set on `keys` (a code with none gets one, no days). */
function keptApart(input: RuleInput, keys: readonly string[]): RoutingRuleRecord[] {
  const records = new Map(input.routingRules.map((r) => [recordKey(r), r]));
  const byKey = new Map(input.customers.map((c) => [c.key, c]));
  for (const key of keys) {
    const had = records.get(key);
    const c = byKey.get(key);
    if (had) records.set(key, { ...had, separate: true });
    else if (c)
      records.set(key, {
        id: "",
        source: c.source,
        code: c.code,
        customerName: c.name,
        openBdays: null,
        dueBdays: null,
        noRule: false,
        separate: true,
        notes: "",
        updatedBy: "",
      });
  }
  return [...records.values()];
}

/** "7 / 3"; "Mixed" for a group whose codes differ. */
const groupText = (r: RoutingRule | null) => (r ? ruleText(r) : "Mixed");

/**
 * The groups codes leave — renamed (`changes`) or kept separate (`separate`) — whose window changes
 * for the codes left in them: e.g. the one leaving set the group's strictest. A warning, never a block.
 */
export function leftBehind(
  input: RuleInput & { changes?: readonly { key: string; shownName: string }[]; separate?: readonly string[] },
): GroupShift[] {
  const changes = input.changes ?? [];
  const before = ruleContext(input);
  const after = ruleContext({
    ...input,
    customerNames: renamed(input, changes),
    routingRules: keptApart(input, input.separate ?? []),
  });
  const shownBefore = (c: RuleInput["customers"][number]) => before.name(c).text;
  const leavingKeys = new Set([
    ...changes
      .filter((ch) => {
        const c = input.customers.find((x) => x.key === ch.key);
        return !!c && !before.separate.has(ch.key) && after.name(c).text !== shownBefore(c);
      })
      .map((ch) => ch.key),
    ...(input.separate ?? []).filter((k) => !before.separate.has(k)),
  ]);
  const leavers = input.customers.filter((c) => leavingKeys.has(c.key));
  const groups = [...new Set(leavers.map(shownBefore))];
  return groups.flatMap((shown) => {
    const left = input.customers.filter(
      (c) => shownBefore(c) === shown && !leavingKeys.has(c.key) && !before.separate.has(c.key),
    );
    if (!left.length) return [];
    const was = left.map((c) => routingRuleFor(c, before));
    const now = left.map((c) => routingRuleFor(c, after));
    if (was.every((w, i) => sameRule(w, now[i]!))) return [];
    const one = (rs: RuleInUse[]) => (rs.every((r) => sameRule(r, rs[0]!)) ? ruleOf(rs[0]!) : null);
    const leaving = leavers.filter((c) => shownBefore(c) === shown).map((c) => c.code);
    const codes = left.map((c) => c.code);
    const [b, a] = [one(was), one(now)];
    return [
      {
        shown,
        codes,
        leaving,
        before: b,
        after: a,
        text: `${shown} (${codes.join(", ")}) will go from ${groupText(b)} to ${groupText(a)} because ${leaving.join(", ")} ${leaving.length === 1 ? "leaves" : "leave"}.`,
      },
    ];
  });
}

/**
 * What saving shown names does to the routing windows (Admin → Customers, before anything is
 * saved): every known customer's rule before and after, the group the saved codes join and its
 * strictest, the groups they leave, and what "Keep their own routing windows" would save.
 * `customers`: the Admin list's; `changes`: each saved code's new shown name ("" is the default).
 */
export function renameImpact(
  input: RuleInput & { changes: readonly { key: string; shownName: string }[] },
): RenameImpact {
  const before = ruleContext(input);
  const after = ruleContext({ ...input, customerNames: renamed(input, input.changes) });
  const rows: (RuleShift & { shownAfter: string })[] = input.customers.map((c) => ({
    key: c.key,
    source: c.source,
    code: c.code,
    name: c.name,
    before: routingRuleFor(c, before),
    after: routingRuleFor(c, after),
    shownAfter: after.name(c).text,
  }));
  const strip = ({ shownAfter: _s, ...r }: RuleShift & { shownAfter: string }): RuleShift => r;
  const shifts = rows.filter((r) => !sameRule(r.before, r.after)).map(strip);
  const byKey = new Map(input.customers.map((c) => [c.key, c]));
  const targets = new Set(
    input.changes.flatMap((ch) => (byKey.has(ch.key) ? [after.name(byKey.get(ch.key)!).text] : [])),
  );
  const shown = targets.size === 1 ? [...targets][0]! : null;
  // A code kept separate is no member: its window never follows the group's.
  const members = shown ? rows.filter((r) => r.shownAfter === shown && !before.separate.has(r.key)).map(strip) : [];
  const joined =
    shown && members.length > 1 ? { shown, members, strictest: strictestRule(members.map((m) => m.before)) } : null;
  const differs = !!joined && joined.members.some((m) => !sameRule(m.before, joined.members[0]!.before));
  const saved = new Set(input.changes.map((ch) => ch.key));
  const leaving = shifts.filter((s) => saved.has(s.key) && !joined?.members.some((m) => m.key === s.key));
  const behind = leftBehind(input);
  return {
    joined,
    shifts,
    leaving,
    leftBehind: behind,
    keepOwn: keepOwnPlan(input),
    dialog: differs ? "share" : leaving.length ? "leave" : behind.length ? "warn" : "none",
  };
}

/**
 * "Keep their own routing windows": each renamed code kept separate from its new group. A code
 * whose own rule (its app days, else its CUST_REF1/2, else the borrow) is not the window it has now
 * keeps that window as its own days (No rule when it has none, `pinned`), so no renamed code's window changes.
 */
export function keepOwnPlan(input: RuleInput & { changes: readonly { key: string; shownName: string }[] }): KeepOwn[] {
  const byKey = new Map(input.customers.map((c) => [c.key, c]));
  const before = ruleContext(input);
  const recordOf = new Map(input.routingRules.map((r) => [recordKey(r), r]));
  return input.changes.flatMap((ch) => {
    const c = byKey.get(ch.key);
    if (!c) return [];
    const now = routingRuleFor(c, before);
    const had = valuesOf(recordOf.get(ch.key));
    // Its own rule once kept separate: its record (or none) with the flag set.
    const kept = { ...(recordOf.get(ch.key) ?? ({} as RoutingRuleRecord)), separate: 1 };
    const alone = routingRuleFor(c, { ...before, app: new Map([...before.app, [ch.key, kept]]) });
    const pinned = !sameRule(alone, now);
    const values: RuleValues = pinned
      ? { ...NO_RULE, ...ruleOf(now), noRule: !hasRule(now), separate: true }
      : { ...had, separate: true };
    return [{ key: c.key, source: c.source, code: c.code, name: c.name, values, pinned }];
  });
}

/** Under "Keep their own routing windows", a code whose window becomes its own days. */
export const pinnedText = (k: Pick<KeepOwn, "code" | "values">) =>
  `${k.code} keeps ${k.values.noRule ? "no rule" : ruleText(k.values)} as its own app rule — it won't follow A2000's CUST_REF any more; Reset in Routing rules brings A2000's back.`;

// ---------------------------------------------------------------------------------------------
// A code joining its group again (Separate off, a separate code's Reset, Join group)

/**
 * The records after `keys` join their group again: Separate off (`reset`: the record emptied —
 * days, No rule and Separate cleared, as Reset saves it).
 */
function rejoined(input: RuleInput, keys: readonly string[], reset: boolean): RoutingRuleRecord[] {
  const ks = new Set(keys);
  return input.routingRules.map((r) =>
    ks.has(recordKey(r))
      ? reset
        ? { ...r, openBdays: null, dueBdays: null, noRule: false, separate: false }
        : { ...r, separate: false }
      : r,
  );
}

/**
 * What codes kept separate joining their group again does to the windows — the same question as a
 * rename into a group (\`renameImpact\`): \`share\` when the group's codes would count with
 * different windows before, with the strictest; else \`none\`. "Keep their own routing windows"
 * there is: don't join.
 */
export function joinImpact(input: RuleInput & { keys: readonly string[]; reset?: boolean }): RenameImpact {
  const before = ruleContext(input);
  const after = ruleContext({ ...input, routingRules: rejoined(input, input.keys, !!input.reset) });
  const byKey = new Map(input.customers.map((c) => [c.key, c]));
  const shownOf = new Set(input.keys.flatMap((k) => (byKey.has(k) ? [before.name(byKey.get(k)!).text] : [])));
  const shown = shownOf.size === 1 ? [...shownOf][0]! : null;
  const rows: RuleShift[] = input.customers.map((c) => ({
    key: c.key,
    source: c.source,
    code: c.code,
    name: c.name,
    before: routingRuleFor(c, before),
    after: routingRuleFor(c, after),
  }));
  const shifts = rows.filter((r) => !sameRule(r.before, r.after));
  const members = shown
    ? rows.filter((r) => before.name(byKey.get(r.key)!).text === shown && !after.separate.has(r.key))
    : [];
  const joined =
    shown && members.length > 1 ? { shown, members, strictest: strictestRule(members.map((m) => m.before)) } : null;
  const differs = !!joined && joined.members.some((m) => !sameRule(m.before, joined.members[0]!.before));
  return {
    joined,
    shifts,
    leaving: [],
    leftBehind: [],
    keepOwn: [],
    dialog: differs || (!!joined && shifts.length > 0) ? "share" : "none",
  };
}

// ---------------------------------------------------------------------------------------------
// The release review: groups whose window changed when routing rules were released

/**
 * The window a code has alone, as before routing rules (and as when kept separate): its own app
 * days, else its own CUST_REF1/2, else the Deposco exact-name borrow, else none.
 */
export function aloneRule(c: RuleCustomer, ctx: RuleContext): RuleInUse {
  const key = customerKey(c.source, c.code);
  const own = ctx.app.get(key);
  const kept = { ...(own ?? ({} as RoutingRuleRecord)), separate: true };
  return routingRuleFor(c, { ...ctx, app: new Map([...ctx.app, [key, kept]]) });
}

/** One code of a group to review: its open lines, its window alone and the group's window now. */
export interface ReviewCode extends RuleCustomerOption {
  separate: boolean;
  alone: RuleInUse;
  now: RuleInUse;
}

/** A group whose window differs from what one of its codes has alone. */
export interface ReviewGroup {
  shown: string;
  codes: ReviewCode[];
  openLines: number;
  /** The strictest of the codes' windows alone: what the group counts with now, without app rules. */
  strictest: RoutingRule;
}

/** The change entry that marks a group reviewed (Admin → Routing rules' release review): its line text. */
export const reviewText = (shown: string) => `Routing review · ${shown}`;
/** A reviewed group's shown name from its entry's line text; null for any other entry. */
export const reviewedShown = (lineText: string) =>
  lineText.startsWith("Routing review · ") ? lineText.slice("Routing review · ".length) : null;

/**
 * The release review: each group (two or more codes) where a code that is not separate counts with
 * a window other than the one it has alone — what changed, unasked, when routing rules were
 * released (BEALLS: 7 / 1 alone, 10 / 3 now). Not those already reviewed (\`reviewed\`: shown names).
 */
export function releaseReview(
  options: readonly RuleCustomerOption[],
  input: Omit<RuleInput, "customers"> & { reviewed?: ReadonlySet<string> },
): ReviewGroup[] {
  const ctx = ruleContext(input);
  const groups = new Map<string, ReviewCode[]>();
  for (const c of options) {
    const shown = ctx.name(c).text;
    const code: ReviewCode = {
      ...c,
      separate: ctx.separate.has(c.key),
      alone: aloneRule(c, ctx),
      now: routingRuleFor(c, ctx),
    };
    groups.set(shown, [...(groups.get(shown) ?? []), code]);
  }
  return [...groups]
    .filter(
      ([shown, codes]) =>
        codes.length > 1 && !input.reviewed?.has(shown) && codes.some((c) => !c.separate && !sameRule(c.alone, c.now)),
    )
    .map(([shown, codes]) => ({
      shown,
      codes: [...codes].sort((a, b) => b.openLines - a.openLines || a.code.localeCompare(b.code)),
      openLines: codes.reduce((n, c) => n + c.openLines, 0),
      strictest: strictestRule(codes.filter((c) => !c.separate).map((c) => c.alone)),
    }))
    .sort((a, b) => b.openLines - a.openLines || nameKey(a.shown).localeCompare(nameKey(b.shown)));
}

/** "Window opens 7 business days before First ship date · Route by 3 business days before Cancel date". */
export function ruleWords(r: RoutingRule): string {
  if (!hasRule(r)) return "No routing rule";
  return [
    r.openBdays === null ? "no Window opens" : `Window opens ${r.openBdays} business days before First ship date`,
    r.dueBdays === null ? "no Route by" : `Route by ${r.dueBdays} business days before Cancel date`,
  ].join(" · ");
}

/** A code's window, before → after, in a few words: "was 3 / 2 → 7 / 3", "had no routing window → 7 / 3". */
export const shiftText = (s: Pick<RuleShift, "before" | "after">) =>
  `${hasRule(s.before) ? `was ${ruleText(s.before)}` : "had no routing window"} → ${hasRule(s.after) ? ruleText(s.after) : "none"}`;

/** "No routing rule" because the app turned the window off (Admin → Routing rules' No rule) for a pick ticket of the line. */
export const ruleTurnedOff = (pts: readonly Pick<PtView, "rule" | "openBdays" | "dueBdays">[]) =>
  pts.some((p) => !!p.rule && isAppRule(p.rule) && p.openBdays === null && p.dueBdays === null);
